import { v4 as uuidv4 } from "uuid";
import { FormConfiguration } from "../../shared/crud/form";
import { Checkbox } from "../../shared/widgets/checkbox";
import SectionUnderline from "../../shared/widgets/sectionUnderline";
import MaxAccommodationsMultiReservation from "../maxAccommodationsMultiReservation";
import { messages } from "../messages";
import {
  AccommodationSortingCriteria,
  BehaviourEntity,
  BundlingConfiguration,
  DepositCheckoutTextConfiguration,
  DepositNotRequiredTextConfiguration,
  DepositRequiredTextConfiguration,
  ExtrasTextConfiguration,
  InventoryGroupingCriteria,
  PendingAmountTextConfiguration,
  PriceCalendar,
  PromosConfig,
  RateType,
  RoomSelectionViewType,
  ShowExtrasConfiguration,
  ShowPromoCodeCriteria,
  SplitStay,
  TaxesText,
} from "./behaviour.entity";
import schema from "./behaviour.schema.json";

export function buildFormConfiguration(): FormConfiguration<BehaviourEntity> {
  return {
    __fieldOrder: [
      "showElementsSection",
      "showBookingForm",
      "showCancelMsg",
      "showLanguageSelector",
      "showCurrencySelector",
      "showAllReservationsIds",
      "showFullReservationIds",
      "hideObligatoryExtras",
      "showBestPriceGuaranteed",
      "showCookiePolicy",
      "showInventoryPriceDiff",
      "waitingListEnabled",
      "showCheckoutButtonRoomUnits",

      "resultSection",
      "hideLocked",
      "hideRestricted",
      "roomSelectionViewType",
      "inventoryGroupingCriteria",
      "splitStayConfiguration",
      "accommodationsOrder",
      "maxRoomsInUnitSelector",
      "roomsLeftAvailabilityLimit",
      "bundlingConfiguration",

      "promosSections",
      "showPromoCodeCriteria",
      "showPromos",
      "showInactiveDiscounts",
      "showRestrictedHighlightMessages",
      "showDiscountsCalculation",

      "textsSection",
      "pendingAmountTextConfiguration",
      "depositRequiredTextConfiguration",
      "depositNotRequiredTextConfiguration",
      "taxesText",
      "depositCheckoutTextConfiguration",
      "extrasTextConfiguration",

      "logoSection",
      "showDesktopLogo",
      "showMobileLogo",
      "isLogoLinkingToPropertyUrl",
      "propertyUrl",
      "enginePageTitle",

      "otherParamsSection",
      "maxAccommodationsMultiReservationSection",
      "allowMultiplyPriceByUnits",
      "showRackRate",
      "hasRoomCombination",
      "addObligatoryExtrasRateToRoomSelection",
      "showDecimalsInExtrasPage",
      "isCancellationReasonRequired",
      "priceCalendar",
      "showExtrasConfiguration",
      "unavailableURL",
      "taxesRateType",
      "overrideCurrencyAccordingToLocation",

      "*",
    ],
    __useProtoStyleArrays: true,
    id: {
      default: () => uuidv4(),
      hidden: true,
    },
    maxAccommodationsMultiReservation: {
      hidden: true,
    },
    showMultipleAccommodations: {
      hidden: true,
    },
    showElementsSection: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.showElementsSection,
      },
    },
    logoSection: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.logoSection,
      },
    },
    textsSection: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.textsSection,
      },
    },
    resultSection: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.resultSection,
      },
    },
    promosSections: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.promosSections,
      },
    },
    otherParamsSection: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.otherParamsSection,
      },
    },
    roomSelectionViewType: {
      messages: {
        label: messages.roomSelectionViewType,
      },
      default: () => RoomSelectionViewType[schema.properties.roomSelectionViewType.enum[0]],
      enumLabels: schema.properties.roomSelectionViewType.enum.map(it => messages[it]),
    },
    inventoryGroupingCriteria: {
      messages: {
        label: messages.inventoryGroupingCriteria,
      },
      default: () => InventoryGroupingCriteria[schema.properties.inventoryGroupingCriteria.enum[0]],
      enumLabels: schema.properties.inventoryGroupingCriteria.enum.map(it => messages[it]),
    },
    splitStayConfiguration: {
      messages: {
        label: messages.splitStayConfiguration,
      },
      default: () => SplitStay[schema.properties.splitStayConfiguration.enum[0]],
      enumLabels: schema.properties.splitStayConfiguration.enum.map(it => messages[it]),
    },
    accommodationsOrder: {
      messages: {
        label: messages.accommodationsOrder,
      },
      default: () => AccommodationSortingCriteria[schema.properties.accommodationsOrder.enum[0]],
      enumLabels: schema.properties.accommodationsOrder.enum.map(it => messages[it]),
    },
    showPromoCodeCriteria: {
      messages: {
        label: messages.showPromoCodeCriteria,
      },
      default: () => ShowPromoCodeCriteria[schema.properties.showPromoCodeCriteria.enum[0]],
      enumLabels: schema.properties.showPromoCodeCriteria.enum.map(it => messages[it]),
    },
    priceCalendar: {
      messages: {
        label: messages.priceCalendar,
      },
      default: () => PriceCalendar[schema.properties.priceCalendar.enum[0]],
      enumLabels: schema.properties.priceCalendar.enum.map(it => messages[it]),
    },
    showExtrasConfiguration: {
      messages: {
        label: messages.showExtrasConfiguration,
      },
      default: () => ShowExtrasConfiguration[schema.properties.showExtrasConfiguration.enum[0]],
      enumLabels: schema.properties.showExtrasConfiguration.enum.map(it => messages[it]),
    },
    pendingAmountTextConfiguration: {
      messages: {
        label: messages.pendingAmountTextConfiguration,
      },
      default: () =>
        PendingAmountTextConfiguration[schema.properties.pendingAmountTextConfiguration.enum[0]],
      enumLabels: schema.properties.pendingAmountTextConfiguration.enum.map(it => messages[it]),
    },
    depositRequiredTextConfiguration: {
      messages: {
        label: messages.depositRequiredTextConfiguration,
      },
      default: () =>
        DepositRequiredTextConfiguration[
          schema.properties.depositRequiredTextConfiguration.enum[0]
        ],
      enumLabels: schema.properties.depositRequiredTextConfiguration.enum.map(it => messages[it]),
    },
    depositNotRequiredTextConfiguration: {
      messages: {
        label: messages.depositNotRequiredTextConfiguration,
      },
      default: () =>
        DepositNotRequiredTextConfiguration[
          schema.properties.depositNotRequiredTextConfiguration.enum[0]
        ],
      enumLabels: schema.properties.depositNotRequiredTextConfiguration.enum.map(
        it => messages[it]
      ),
    },
    taxesText: {
      messages: {
        label: messages.taxesText,
      },
      default: () => TaxesText.TAXES_INCLUDED,
      enumLabels: schema.properties.taxesText.enum.map(it => messages[it]),
    },
    depositCheckoutTextConfiguration: {
      messages: {
        label: messages.depositCheckoutTextConfiguration,
      },
      default: () => DepositCheckoutTextConfiguration.DEPOSIT,
      enumLabels: schema.properties.depositCheckoutTextConfiguration.enum.map(it => messages[it]),
    },
    extrasTextConfiguration: {
      messages: {
        label: messages.extrasTextConfiguration,
      },
      default: () => ExtrasTextConfiguration.EXTRAS,
      enumLabels: schema.properties.extrasTextConfiguration.enum.map(it => messages[it]),
    },
    showBookingForm: {
      messages: {
        label: messages.showBookingForm,
      },
      widget: Checkbox,
      default: () => true,
    },
    showDesktopLogo: {
      messages: {
        label: messages.showDesktopLogo,
      },
      widget: Checkbox,
      default: () => true,
    },
    showMobileLogo: {
      messages: {
        label: messages.showMobileLogo,
      },
      widget: Checkbox,
      default: () => true,
    },
    showPromos: {
      messages: {
        label: messages.showPromos,
      },
      widget: Checkbox,
      default: () => true,
    },
    showCancelMsg: {
      messages: {
        label: messages.showCancelMsg,
      },
      widget: Checkbox,
      default: () => true,
    },
    showLanguageSelector: {
      messages: {
        label: messages.showLanguageSelector,
      },
      widget: Checkbox,
      default: () => true,
    },
    showCurrencySelector: {
      messages: {
        label: messages.showCurrencySelector,
      },
      widget: Checkbox,
      default: () => true,
    },
    showDiscountsCalculation: {
      messages: {
        label: messages.showDiscountsCalculation,
      },
      widget: Checkbox,
      default: () => true,
    },
    showAllReservationsIds: {
      messages: {
        label: messages.showAllReservationsIds,
      },
      widget: Checkbox,
      default: () => true,
    },
    showFullReservationIds: {
      messages: {
        label: messages.showFullReservationIds,
      },
      widget: Checkbox,
      default: () => true,
    },
    hideObligatoryExtras: {
      messages: {
        label: messages.hideObligatoryExtras,
      },
      widget: Checkbox,
      default: () => true,
    },
    showBestPriceGuaranteed: {
      messages: {
        label: messages.showBestPriceGuaranteed,
      },
      widget: Checkbox,
      default: () => true,
    },
    maxAccommodationsMultiReservationSection: {
      fieldWidget: MaxAccommodationsMultiReservation,
    },
    showCookiePolicy: {
      messages: {
        label: messages.showCookiePolicy,
      },
      widget: Checkbox,
      default: () => true,
    },
    showInventoryPriceDiff: {
      messages: {
        label: messages.showInventoryPriceDiff,
      },
      widget: Checkbox,
      default: () => true,
    },
    showInactiveDiscounts: {
      messages: {
        label: messages.showInactiveDiscounts,
      },
      default: () => PromosConfig.SHOW_WHEN_APPLICABLE,
      enumLabels: schema.properties.showInactiveDiscounts.enum.map(it => messages[it]),
    },
    showRestrictedHighlightMessages: {
      messages: {
        label: messages.showRestrictedHighlightMessages,
      },
      default: () => PromosConfig.SHOW_WHEN_APPLICABLE,
      enumLabels: schema.properties.showRestrictedHighlightMessages.enum.map(it => messages[it]),
    },
    isLogoLinkingToPropertyUrl: {
      messages: {
        label: messages.isLogoLinkingToPropertyUrl,
      },
      widget: Checkbox,
      default: () => true,
    },
    maxRoomsInUnitSelector: {
      messages: {
        label: messages.maxRoomsInUnitSelector,
      },
    },
    roomsLeftAvailabilityLimit: {
      messages: {
        label: messages.roomsLeftAvailabilityLimit,
      },
    },
    unavailableURL: {
      messages: {
        label: messages.unavailableURL,
      },
    },
    taxesRateType: {
      messages: {
        label: messages.taxesRateType,
      },
      default: () => RateType[schema.properties.taxesRateType.enum[0]],
      enumLabels: schema.properties.taxesRateType.enum.map(it => messages[it]),
    },
    enginePageTitle: {
      messages: {
        label: messages.enginePageTitle,
      },
    },
    propertyUrl: {
      messages: {
        label: messages.propertyUrl,
      },
    },
    hideLocked: {
      messages: {
        label: messages.hideLocked,
      },
      widget: Checkbox,
      default: () => true,
    },
    hideRestricted: {
      messages: {
        label: messages.hideRestricted,
      },
      widget: Checkbox,
      default: () => true,
    },
    showRackRate: {
      messages: {
        label: messages.showRackRate,
      },
      widget: Checkbox,
      default: () => true,
    },
    allowMultiplyPriceByUnits: {
      messages: {
        label: messages.allowMultiplyPriceByUnits,
      },
      widget: Checkbox,
      default: () => false,
    },
    hasRoomCombination: {
      messages: {
        label: messages.hasRoomCombination,
      },
      widget: Checkbox,
      default: () => true,
    },
    addObligatoryExtrasRateToRoomSelection: {
      messages: {
        label: messages.addObligatoryExtrasRateToRoomSelection,
      },
      widget: Checkbox,
      default: () => true,
    },
    showDecimalsInExtrasPage: {
      messages: {
        label: messages.showDecimalsInExtrasPage,
      },
      widget: Checkbox,
      default: () => false,
    },
    isCancellationReasonRequired: {
      messages: {
        label: messages.isCancellationReasonRequired,
      },
      widget: Checkbox,
      default: () => false,
    },
    bundlingConfiguration: {
      messages: {
        label: messages.bundlingConfiguration,
      },
      default: () => BundlingConfiguration[schema.properties.bundlingConfiguration.enum[0]],
      enumLabels: schema.properties.bundlingConfiguration.enum.map(it => messages[it]),
    },
    waitingListEnabled: {
      messages: {
        label: messages.waitingListEnabled,
      },
      widget: Checkbox,
      default: () => false,
    },
    showCheckoutButtonRoomUnits: {
      messages: {
        label: messages.showCheckoutButtonRoomUnits,
      },
      widget: Checkbox,
      default: () => false,
    },
    overrideCurrencyAccordingToLocation: {
      messages: {
        label: messages.overrideCurrencyAccordingToLocation,
      },
      widget: Checkbox,
      default: () => false,
    },
  };
}
